// AGC: FarmProHub Staking Page
// AGC: Full staking interface - stake, unstake, view position
// AGC: Uses same StakingManagerV5 contract as Origami Battles

import React, { useState } from 'react'
import { useAccount, useReadContract } from 'wagmi'
import { motion } from 'framer-motion'
import { Coins, TrendingUp, Lock, Unlock, Clock, Users, ArrowUpRight, ArrowDownLeft, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react'
import { useStaking } from '../hooks/useStaking'
import { formatEther, parseEther, parseAbi } from 'viem'

// AGC: StakingMirror contract address and ABI
const STAKING_MIRROR_ADDRESS = (import.meta.env.VITE_STAKING_MIRROR_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const STAKING_MIRROR_ABI = parseAbi([
  'function getStakedBalance() external view returns (uint256)',
])

// AGC: BaseScan URL helper
const getBaseScanUrl = (address: string) => {
  const network = import.meta.env.VITE_BASE_NETWORK || 'base-sepolia'
  if (network === 'base' || network === 'base-mainnet') {
    return `https://basescan.org/address/${address}`
  }
  return `https://sepolia.basescan.org/address/${address}`
}

export const StakingPage: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { stats, stake, requestUnstake, ogbBalance, isStaking, isUnstaking, refresh } = useStaking()
  const [stakeAmount, setStakeAmount] = useState('')
  const [unstakeAmount, setUnstakeAmount] = useState('')
  const [activeTab, setActiveTab] = useState<'stake' | 'unstake'>('stake')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // AGC: Read StakingMirror staked balance
  const { data: mirrorStakedBalance } = useReadContract({
    address: STAKING_MIRROR_ADDRESS,
    abi: STAKING_MIRROR_ABI,
    functionName: 'getStakedBalance',
    query: {
      enabled: STAKING_MIRROR_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Format numbers for display
  const formatNumber = (value: bigint | number, decimals: number = 2) => {
    if (typeof value === 'bigint') {
      return Number(formatEther(value)).toFixed(decimals)
    }
    return value.toFixed(decimals)
  }

  // AGC: Handle stake
  const handleStake = async () => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      setError('Please enter a valid amount')
      return
    }

    try {
      setError(null)
      setSuccess(null)
      const amount = parseEther(stakeAmount)
      await stake(amount)
      setSuccess(`Successfully staked ${stakeAmount} OGB!`)
      setStakeAmount('')
      refresh()
    } catch (err: any) {
      setError(err.message || 'Failed to stake')
    }
  }

  // AGC: Handle unstake request
  const handleUnstake = async () => {
    if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) {
      setError('Please enter a valid amount')
      return
    }

    try {
      setError(null)
      setSuccess(null)
      const amount = parseEther(unstakeAmount)
      await requestUnstake(amount)
      setSuccess(`Unstake request submitted for ${unstakeAmount} OGB!`)
      setUnstakeAmount('')
      refresh()
    } catch (err: any) {
      setError(err.message || 'Failed to request unstake')
    }
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4 relative"
        >
          {/* AGC: FPscore Panel - Top Right */}
          {isConnected && (
            <div className="absolute top-0 right-0">
              <div className="bg-slate-800/80 border border-slate-700/50 rounded-lg px-3 py-1.5">
                <span className="text-xs font-semibold text-white">FPscore: </span>
                <span className="text-xs font-semibold text-[#0072CE]">
                  {stats.stakingScore ? (Number(formatEther(stats.stakingScore)) * 100).toFixed(2) : '0.00'}%
                </span>
              </div>
            </div>
          )}
          <h1 className={`text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2 ${isConnected ? 'pt-10' : ''}`}>
            <Coins className="w-7 h-7 text-[#0072CE]" />
            Staking
          </h1>
          <p className="text-slate-400 text-sm">
            Stake FarmPro tokens to earn 50% of all project revenues
          </p>
        </motion.div>

        {/* AGC: Error/Success Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-900/30 border border-red-500/50 rounded-lg p-3 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-sm">{error}</p>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-900/30 border border-green-500/50 rounded-lg p-3 flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-green-400 text-sm">{success}</p>
          </motion.div>
        )}

        {/* AGC: Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 gap-3"
        >
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <Coins className="w-5 h-5 text-[#0072CE] mx-auto mb-2" />
            <p className="text-xs text-slate-400">Total Staked</p>
            <p className="text-lg font-bold text-white">{formatNumber(stats.totalStaked)} OGB</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Your Share</p>
            <p className="text-lg font-bold text-white">{stats.yourShare.toFixed(2)}%</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center col-span-2 sm:col-span-1">
            <TrendingUp className="w-5 h-5 text-[#0072CE] mx-auto mb-2" />
            <p className="text-xs text-slate-400">Revenue Share</p>
            <p className="text-lg font-bold text-[#0072CE]">50%</p>
          </div>
        </motion.div>

        {/* AGC: StakingMirror Info */}
        {STAKING_MIRROR_ADDRESS !== '0x0000000000000000000000000000000000000000' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-purple-400" />
                <h4 className="text-white font-semibold text-sm">Backend Stake (50%)</h4>
              </div>
              <a
                href={getBaseScanUrl(STAKING_MIRROR_ADDRESS)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#0072CE] hover:text-[#0088e6] flex items-center gap-1"
              >
                View Contract
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-xs">
                StakingMirror Contract Staked
              </span>
              <span className="text-white font-semibold">
                {mirrorStakedBalance ? formatNumber(mirrorStakedBalance) : '0.00'} OGB
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              The StakingMirror contract maintains a 50% backend stake to match player stakes, ensuring balanced revenue distribution.
            </p>
          </motion.div>
        )}

        {/* AGC: Your Position */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-[#003a5e]/30 to-[#003a5e]/30 rounded-xl p-5 border border-[#0072CE]/20"
          >
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              {stats.isStaker ? (
                <Lock className="w-4 h-4 text-[#0072CE]" />
              ) : (
                <Unlock className="w-4 h-4 text-slate-500" />
              )}
              Your Staking Position
              {stats.isStaker && (
                <span className="ml-auto text-xs bg-[#0072CE]/20 text-[#0072CE] px-2 py-1 rounded">
                  Staker
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-400">Your Stake</p>
                <p className="text-xl font-bold text-white">{formatNumber(stats.yourStake)} OGB</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Your Share</p>
                <p className="text-xl font-bold text-[#0072CE]">{stats.yourShare.toFixed(2)}%</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">FPscore</p>
                <p className="text-xl font-bold text-white">
                  {stats.stakingScore ? (Number(formatEther(stats.stakingScore)) * 100).toFixed(2) : '0.00'}%
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Tier</p>
                <p className="text-xl font-bold text-white">
                  {stats.tier === 0 ? 'None' : stats.tier === 1 ? 'Access' : 'Max'}
                </p>
              </div>
            </div>
            {stats.pendingUnstakes > 0 && (
              <div className="mt-4 bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
                <p className="text-yellow-400 text-xs flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  You have {stats.pendingUnstakes} pending unstake request(s)
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* AGC: Stake/Unstake Interface */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          {/* AGC: Tab Selector */}
          <div className="flex mb-4 bg-slate-700/50 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('stake')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'stake'
                  ? 'bg-[#005a9e] text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <ArrowUpRight className="w-4 h-4" />
              Stake
            </button>
            <button
              onClick={() => setActiveTab('unstake')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'unstake'
                  ? 'bg-red-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <ArrowDownLeft className="w-4 h-4" />
              Unstake
            </button>
          </div>

          {/* AGC: Stake Form */}
          {activeTab === 'stake' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Amount to Stake</label>
                <div className="relative">
                  <input
                    type="number"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-[#0072CE]"
                  />
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#0072CE] hover:text-[#0088e6]">
                    MAX
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Balance: {formatNumber(ogbBalance)} OGB</p>
              </div>
              <button
                onClick={handleStake}
                disabled={!isConnected || isStaking || !stakeAmount || parseFloat(stakeAmount) <= 0}
                className="w-full py-3 bg-[#005a9e] hover:bg-[#004a7e] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isStaking ? 'Staking...' : isConnected ? 'Stake OGB Tokens' : 'Connect Wallet to Stake'}
              </button>
            </div>
          )}

          {/* AGC: Unstake Form */}
          {activeTab === 'unstake' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Amount to Unstake</label>
                <div className="relative">
                  <input
                    type="number"
                    value={unstakeAmount}
                    onChange={(e) => setUnstakeAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-red-500"
                  />
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-400 hover:text-red-300">
                    MAX
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Staked: {formatNumber(stats.yourStake)} OGB</p>
              </div>
              <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-xs flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Unstaking requires a 3-day cooldown period. You'll need to complete the unstake after the cooldown.
                </p>
              </div>
              <button
                onClick={handleUnstake}
                disabled={!isConnected || isUnstaking || !unstakeAmount || parseFloat(unstakeAmount) <= 0 || stats.yourStake === 0n}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUnstaking ? 'Requesting Unstake...' : isConnected ? 'Request Unstake' : 'Connect Wallet to Unstake'}
              </button>
            </div>
          )}
        </motion.div>

        {/* AGC: Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-3">How Staking Works</h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">1.</span>
              Stake your FarmPro (FP) tokens to become a stakeholder
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">2.</span>
              Earn 50% of all revenue generated by FarmProHub projects
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">3.</span>
              Revenue is distributed proportionally based on your stake
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">4.</span>
              Participate in governance by voting on proposals
            </li>
          </ul>
        </motion.div>
      </div>
    </main>
  )
}

